import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';
import { useTeacherProfile } from '@/contexts/TeacherProfileContext';
import { useTeacherInquiries, useMyInquiries } from '@/hooks/use-inquiries';
import { useCurrentUser } from '@/hooks/use-current-user';
import { getOfferings } from '@/services/OfferingServices';
import {
  deriveTeacherSteps,
  deriveStudentSteps,
  allStepsDone,
  type NextStep,
} from '@/lib/nextSteps';

// ── Persistence ──────────────────────────────────────────────────────────────

type Role = 'teacher' | 'student';

interface NextStepsState {
  dismissed?: boolean;
  hasSearched?: boolean;
}

const stateKey = (role: Role) => `bmc:next-steps:${role}`;

function readState(role: Role): NextStepsState {
  try {
    return JSON.parse(localStorage.getItem(stateKey(role)) ?? '{}');
  } catch {
    return {};
  }
}

function writeState(role: Role, patch: NextStepsState) {
  try {
    localStorage.setItem(stateKey(role), JSON.stringify({ ...readState(role), ...patch }));
  } catch {
    // storage unavailable — non-critical
  }
}

/** Call on search page mount so the student "Browse teachers" step completes. */
export function markSearchVisited() {
  writeState('student', { hasSearched: true });
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export interface UseNextStepsResult {
  steps: NextStep[];
  allDone: boolean;
  /** true once data has settled enough to trust the steps */
  isReady: boolean;
  dismissed: boolean;
  dismiss: () => void;
}

/** Teacher offerings list; shared by NextStepsCard and the offering nudge banner. */
export function useTeacherOfferingsQuery() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const hasLocalIdentity = typeof document !== 'undefined' && document.cookie.includes('X-Local-Identity=');
  return useQuery({
    queryKey: ['teacherOfferings'],
    queryFn: () => getOfferings(token),
    enabled: auth.isAuthenticated || hasLocalIdentity,
    staleTime: 60 * 1000,
  });
}

export function useTeacherNextSteps(): UseNextStepsResult {
  const { hasDraft, hasActiveVersion, draftStatus, isLoading: profileLoading } = useTeacherProfile();
  const { data: inquiriesData, isLoading: inquiriesLoading } = useTeacherInquiries(0, 20);

  const { data: offeringsData, isLoading: offeringsLoading } = useTeacherOfferingsQuery();

  const offerings = offeringsData?.offerings ?? [];
  const threads = inquiriesData?.content ?? [];

  const steps = deriveTeacherSteps(
    {
      hasProfile: hasDraft || hasActiveVersion,
      profileApproved: hasActiveVersion,
      profilePendingReview: !hasActiveVersion && draftStatus === 'PENDING_REVIEW',
      profileRejected: draftStatus === 'REJECTED',
      offeringCount: offerings.length,
      hasPublishedOffering: offerings.some((o) => o.status === 'PUBLISHED'),
      hasInquiries: (inquiriesData?.totalElements ?? threads.length) > 0,
      hasRepliedToInquiry: threads.some(
        (t) => t.status === 'TEACHER_REPLIED' || t.status === 'ACTIVE',
      ),
    },
    {
      profileSetup: '/teacher-profile-setup',
      createOffering: '/create-offering',
      offerings: '/teacher-offerings',
      inquiries: '/teacher-inquiries',
    },
  );

  return useDismissable('teacher', steps, !profileLoading && !offeringsLoading && !inquiriesLoading);
}

export function useStudentNextSteps(): UseNextStepsResult {
  const { user, isLoading: userLoading } = useCurrentUser();
  const { data: inquiriesData, isLoading: inquiriesLoading } = useMyInquiries(0, 20);

  const threads = inquiriesData?.content ?? [];

  const steps = deriveStudentSteps(
    {
      goalCompleted: user?.goalOnboardingCompleted === true,
      hasSearched: readState('student').hasSearched === true,
      hasInquiries: (inquiriesData?.totalElements ?? threads.length) > 0,
      hasTeacherReply: threads.some(
        (t) => t.status === 'TEACHER_REPLIED' || t.status === 'ACTIVE' || t.unreadCount > 0,
      ),
    },
    {
      goalOnboarding: '/goal-onboarding',
      search: '/search',
      inquiries: '/my-inquiries',
    },
  );

  return useDismissable('student', steps, !userLoading && !inquiriesLoading);
}

function useDismissable(role: Role, steps: NextStep[], isReady: boolean): UseNextStepsResult {
  const [dismissed, setDismissed] = useState(() => readState(role).dismissed === true);
  const allDone = allStepsDone(steps);

  const dismiss = useCallback(() => {
    writeState(role, { dismissed: true });
    setDismissed(true);
  }, [role]);

  // Dismissal only sticks once everything is done; if a step regresses, show again.
  return { steps, allDone, isReady, dismissed: dismissed && allDone, dismiss };
}
